import {
  CustomFieldEntityEnum,
  ICustomFieldRecordSchema,
  ICustomFieldResSchema,
  IQuoteRequestSchema,
  QuoteStatusEnum,
} from '@monetize/types/app';
import { ReactNode, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  CustomFieldDataForm,
  CustomFieldDataFormProps,
} from '../../../../components/CustomFields/CustomFieldDataForm';
import { QuoteFieldAnchor } from '../../../../components/Quotes/QuoteFieldAnchor';
import { useSerializedMutation } from '../../../../hooks/useSerializedMutation';
import { useQuoteContext } from '../quoteContext';

/** The anchor encloses the control, so jumping to a custom field can focus it. */
const anchorCustomField = (
  { key }: ICustomFieldResSchema,
  field: ReactNode,
) => (
  <QuoteFieldAnchor fieldKey={`customFields.${key}`}>{field}</QuoteFieldAnchor>
);

export const QuoteCustomFields = ({
  ...rest
}: Partial<CustomFieldDataFormProps>) => {
  const { quoteData, isReadOnly } = useQuoteContext();
  const { setValue } = useFormContext<IQuoteRequestSchema>();

  const quoteId = quoteData.quote?.id;
  const { updateQuoteCustomFields, evaluateQuoteRules } = quoteData;

  // Defers to `quoteData.updateQuoteCustomFields`, which runs the request on
  // the per-quote chain and toggles `saving`. `useSerializedMutation`
  // coalesces rapid input bursts (e.g., tabbing through fields) into a single
  // follow-up call with the latest value. The re-evaluation must go through the
  // quote action queue too: a quote update queued behind the custom field save
  // would otherwise start at the same moment and race it on the server.
  const persistCustomFields = useCallback(
    async (customFields: ICustomFieldRecordSchema) => {
      if (!quoteId) {
        return;
      }
      await updateQuoteCustomFields(quoteId, customFields);
      if (quoteData.quote?.status === QuoteStatusEnum.DRAFT) {
        await evaluateQuoteRules(quoteId);
      }
    },
    [
      quoteId,
      updateQuoteCustomFields,
      evaluateQuoteRules,
      quoteData.quote?.status,
    ],
  );

  const queueCustomFieldsUpdate = useSerializedMutation(persistCustomFields);

  if (
    !quoteData.quote?.customFields ||
    !quoteData.quote ||
    !quoteData.quote?.id
  ) {
    return null;
  }

  const handleValueUpdate = (
    customFields: ICustomFieldRecordSchema,
    shouldPersist = true,
  ) => {
    setValue('customFields', customFields);
    if (shouldPersist) {
      queueCustomFieldsUpdate(customFields);
    }
  };

  return (
    <CustomFieldDataForm
      entity={CustomFieldEntityEnum.QUOTE}
      value={quoteData.quote?.customFields}
      setValue={handleValueUpdate}
      mode="accordion"
      mt="4"
      fieldColSpan={4}
      isReadOnly={isReadOnly}
      renderFieldWrapper={anchorCustomField}
      showDivider
      {...rest}
    />
  );
};
